import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Grid, Divider } from '@mui/material';
import {
  BusinessCenter,
  AttachMoney,
  Assignment,
  Person,
  CalendarToday,
} from '@mui/icons-material';
import { SGR_TYPES, SGR_STATES } from 'lib/consts';
import { formatDate, formatMoney } from 'lib/functions/utils';
import DetailedRiskPremiumCalculator from './DetailedRiskPremiumCalculator';
import PaymentDocument from './PaymentDocument';
import type { RiskPremiumRates } from 'lib/functions/riskPremiumCalculator';
import { Warranty } from 'lib/types/models';
import CustomButton from 'lib/designSystem/Button';

interface SummaryProps {
  warranty: Warranty;
  setIsEditing: (isEditing: boolean) => void;
}

const Summary: React.FC<SummaryProps> = ({ warranty, setIsEditing }) => {
  const warrantyType =
    SGR_TYPES.find((type) => type.value === warranty.type)?.label ||
    warranty.type;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const [rateControls, setRateControls] = useState<RiskPremiumRates>({
    variableRate: warranty.operationCostVariable,
    fixedRate: warranty.operationCostFixed,
    variableDiscount: warranty.discountRateVariable,
    fixedDiscount: warranty.discountRateFixed,
  });
  const document = warranty.documents?.[0];
  const showDocument = document ? true : false;
  const showCalculator = !document ? true : false;
  const canEdit =
    warranty.status === 'REJECTED' ||
    warranty.status === 'APPROVED' ||
    warranty.status === 'IN_REVIEW';

  const amount = warranty.fixedAmount || warranty.quotedAmount;
  const daysDifference = Math.ceil(
    (new Date(warranty.endDate).getTime() -
      new Date(warranty.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper
              sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1rem',
                    lineHeight: 1.1,
                  }}
                >
                  Información General
                </Typography>
                <Chip
                  label="Detalles"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: '18px', fontSize: '0.65rem' }}
                />
                <Chip
                  label={warrantyStatus?.label}
                  size="small"
                  color={warrantyStatus?.color as any}
                  sx={{ height: '18px', fontSize: '0.65rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BusinessCenter
                      sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Tipo de Garantía
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {warrantyType}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AttachMoney
                      sx={{ fontSize: '0.8rem', color: 'success.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Monto Cotizado
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatMoney(warranty.quotedAmount)}{' '}
                      {warranty.quotedCurrency}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 152, 0, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Assignment
                      sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Número de Licitación
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      #{warranty.tenderId}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(156, 39, 176, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Person
                      sx={{ fontSize: '0.8rem', color: 'secondary.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      RUT Mandante
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {warranty.principalRut}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
            <Paper
              sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: '1rem',
                    lineHeight: 1.1,
                  }}
                >
                  Fechas Importantes
                </Typography>
                <Chip
                  label="Cronología"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: '18px', fontSize: '0.65rem' }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Fecha de Cotización
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatDate(new Date(warranty.quoteDate), 'dd-mm-yyyy')}
                    </Typography>
                  </Box>
                </Box>
                <Divider />
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: '0.8rem', color: 'success.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Fecha de Inicio
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatDate(new Date(warranty.startDate), 'dd-mm-yyyy')}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: '0.8rem', color: 'error.main' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Fecha de Término
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatDate(new Date(warranty.endDate), 'dd-mm-yyyy')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          {showCalculator && (
            <DetailedRiskPremiumCalculator
              amount={amount}
              days={daysDifference}
              conversionRate={warranty.conversionRate || 1}
              type={warranty.type}
              businessIdentifier={warranty.businessIdentifier}
              rateControls={rateControls}
              setRateControls={setRateControls}
              useConcentrationRates={false}
              canEditRates={false}
            />
          )}
          {showDocument && (
            <PaymentDocument
              document={warranty.documents?.[0]}
              warranty={warranty}
            />
          )}
        </Grid>
      </Grid>
      {canEdit && (
        <Box sx={{ position: 'absolute', right: 20, bottom: 20 }}>
          <CustomButton
            label="Editar"
            onClick={() => setIsEditing(true)}
            color="primary"
            variant="contained"
          >
            Editar
          </CustomButton>
        </Box>
      )}
    </Box>
  );
};

export default Summary;
